import { useCallback, useMemo } from 'react'

import { useUnlockedChannels } from '~publish/contexts/AccountContext'
import {
  draftDataToRpcUpdate,
  getDraftDataFromLocalStorage,
} from '~publish/legacy/composer/composer/utils/PreviewUtils'
import type { SidePanel } from '~publish/components/Composer/store/appSlice'

import { useComposerDialogStore } from './composerDialogStore'
import { useFetchPostForComposer } from './postForComposer/useFetchPostForComposer'
import type { OpenComposerInput, PrefillPostData } from './composerDialogStore'

export type { PrefillPostData } from './composerDialogStore'

// Starts ComposerDialog's chunk fetch before openComposer() would (see
// App/index.tsx's lazyPage gate). Specifier must match that call exactly —
// dynamic import() can't share a string constant across call sites.
export function preloadComposerDialog(): void {
  void import('~publish/components/Composer')
}

/**
 * Strip Mastodon markdown URL annotations from text.
 * Mastodon API returns hashtags/mentions as HTML anchors; the html-to-text
 * converter historically stored them as `#tag [https://…]`. This removes
 * the bracketed URL portion so duplicated posts get clean text.
 *
 * @see graphql/src/post/adapters/generateAnnotations.ts mastodonMarkdownUrlRegexPattern
 */
const stripMastodonMarkdownUrls = (text: string): string =>
  text.replace(/(@\S+|#\S+|https?:\/\/\S+)\s*\[(https?:\/\/[^\]]+)\]/g, '$1')

export type OpenComposerCommonOptions = {
  aiPanelOpen?: boolean
  prefillFromLocalStorage?: boolean
  activeSidePanel?: SidePanel
  prefillAiPrompt?: string
  draftMode?: boolean
}

type EditPostInComposerData = {
  cta?: CTA
  postId: string
  // The ContentItem/PostGroup this post belongs to, when the caller already
  // has it (e.g. PostCard_Post's `postGroup`) — powers the Header's title
  // editor for channel-specific posts, same as the channel-less draft path.
  contentItemId?: string
  contentItemTitle?: string | null
}

type EditContentItemDraftInComposerData = {
  cta?: CTA
  contentItemId: string
  contentItemTitle?: string | null
  prefillPostData?: PrefillPostData
}

type DuplicatePostInComposerData = {
  cta?: CTA
  channels?: string[] | 'all'
  prefillPostData: PrefillPostData
  duplicatedFrom: string
}

type CreateNewPinnedPostInComposerData = {
  cta?: CTA
  channels: [string]
  prefillPostData: {
    dueAt: number
    pinned: true
    tags?: string[]
  }
}

export type CreateNewPostInComposerData = {
  cta?: CTA
  returnFocusTo?: HTMLElement | null
  channels?: string[] | 'all'
  prefillPostData?: PrefillPostData
  templateId?: string
}

export type UseComposerResult = {
  // This is the object that should be spread on the trigger element. It
  // contains all the necessary attributes for the trigger element to be accessible.
  triggerAttributes: {
    'aria-haspopup': 'dialog'
    'aria-expanded': boolean
    'aria-controls': 'composer-root'
    'data-state': 'open' | 'closed'
  }
  isOpen: boolean
  editPostInComposer: (
    data: EditPostInComposerData,
    options?: OpenComposerCommonOptions,
  ) => Promise<void>
  editContentItemDraftInComposer: (
    data: EditContentItemDraftInComposerData,
    options?: OpenComposerCommonOptions,
  ) => Promise<void>
  duplicatePostInComposer: (
    data: DuplicatePostInComposerData,
    options?: OpenComposerCommonOptions,
  ) => Promise<void>
  createNewPinnedPostInComposer: (
    data: CreateNewPinnedPostInComposerData,
    options?: OpenComposerCommonOptions,
  ) => Promise<void>
  createNewPostInComposer: (
    data: CreateNewPostInComposerData,
    options?: OpenComposerCommonOptions,
  ) => Promise<void>
  hideComposer: () => void
  discardComposer: () => void
}

/**
 * Provides a hook for managing the opening and configuration of a post composer modal.
 * This hook simplifies the process of configuring and triggering the post composer modal
 * across different parts of the application. It ensures accessibility by managing relevant ARIA attributes
 * and also handles the state of the modal (open or closed).
 *
 * @returns {UseComposerResult} An object containing the trigger attributes for accessibility,
 * the current open state of the composer, and a function to open the composer.
 *
 * @example
 * // Example of using usePostComposer in a component to create a new post
 * const { triggerAttributes, isOpen, createNewPostInComposer } = usePostComposer();
 *
 * <button {...triggerAttributes} onClick={() => createNewPostInComposer({
 *   cta: 'publish-allChannels-topNav-newPost-1',
 * })}>
 *   Open Composer
 * </button>
 *
 * @example
 * // Handling edit post scenario
 * const { triggerAttributes, editPostInComposer } = usePostComposer();
 *
 * <button {...triggerAttributes} onClick={() => editPostInComposer({
 *   cta: 'publish-allChannels-postCard-editPost-1',
 *   postId: '1234',
 * })}>
 *   Edit Post
 * </button>
 *
 * @example
 * // Handling create post scenario with text prefill for all channels
 * const { triggerAttributes, createNewPostInComposer } = usePostComposer();
 *
 * <button {...triggerAttributes} onClick={() => createNewPostInComposer({
 *  cta: 'publish-allChannels-topNav-newPost-1',
 *  channels: 'all',
 *  prefillPostData: {
 *    text: 'This is a pre-filled text for the post'
 *  }
 * })}>
 *  Create Post
 * </button>
 *
 * @example
 * // Handling create post scenario when duplicating a post
 * const { triggerAttributes, duplicatePostInComposer } = usePostComposer();
 *
 * <button {...triggerAttributes} onClick={() => duplicatePostInComposer({
 *  cta: 'publish-allChannels-topNav-newPost-1',
 *  channels: 'all',
 *  prefillPostData: RpcUpdate,
 *  duplicatedFrom: RpcUpdate.id,
 * })}>
 *  Duplicate Post
 * </button>
 *
 * @example
 * // Handling create post scenario from clicking on a queue slot
 * const { triggerAttributes, createNewPinnedPostInComposer } = usePostComposer();
 *
 * <button {...triggerAttributes} onClick={() => createNewPinnedPostInComposer({
 *  cta: 'publish-allChannels-topNav-newPost-1',
 *  channels: ['channelId'],
 *  prefillPostData: {
 *    dueAt: 1721724196,
 *    pinned: true
 *  }
 * })}>
 *  Create Post
 * </button>
 */
export function usePostComposer(): UseComposerResult {
  const channels = useUnlockedChannels()
  const open = useComposerDialogStore((state) => state.showComposer)
  const openComposer = useComposerDialogStore((state) => state.openComposer)
  const handleEditClick = useComposerDialogStore(
    (state) => state.handleEditClick,
  )
  const hideComposerAction = useComposerDialogStore(
    (state) => state.hideComposer,
  )
  const fetchPost = useFetchPostForComposer()

  const triggerAttributes = useMemo(
    () => ({
      'aria-haspopup': 'dialog' as const,
      'aria-expanded': open,
      'aria-controls': 'composer-root' as const,
      'data-state': open ? ('open' as const) : ('closed' as const),
    }),
    [open],
  )

  const editPostInComposer = useCallback(
    async (
      data: EditPostInComposerData,
      options?: OpenComposerCommonOptions,
    ): Promise<void> => {
      void handleEditClick(data.postId, {
        cta: data.cta,
        withAIPanelOpen: options?.aiPanelOpen,
        contentItemId: data.contentItemId,
        contentItemTitle: data.contentItemTitle,
        fetchPost,
      })
    },
    [handleEditClick, fetchPost],
  )

  const editContentItemDraftInComposer = useCallback(
    async (
      data: EditContentItemDraftInComposerData,
      options?: OpenComposerCommonOptions,
    ): Promise<void> => {
      // channels: [] opens the channel-less omnibox; contentItemId seeds
      // omni.draftId so Save routes to updateContentItemDraft (edit in place).
      // The dueAt→schedule mapping mirrors createNewPostInComposer, so a prefill
      // dueAt seeds the schedule selector with the draft's existing targetDate.
      openComposer({
        selectedProfileIds: [],
        cta: data.cta,
        withAIPanelOpen:
          options?.aiPanelOpen || options?.activeSidePanel === 'aiAssistant',
        activeSidePanel: options?.activeSidePanel,
        draftMode: options?.draftMode,
        contentItemId: data.contentItemId,
        contentItemTitle: data.contentItemTitle,
        prefillPostData: data.prefillPostData && {
          ...data.prefillPostData,
          ...(data.prefillPostData.dueAt && {
            due_at: data.prefillPostData.dueAt,
            scheduled_at: data.prefillPostData.dueAt,
            scheduledAt: data.prefillPostData.dueAt,
          }),
        },
      })
    },
    [openComposer],
  )

  const duplicatePostInComposer = useCallback(
    async (
      data: DuplicatePostInComposerData,
      options?: OpenComposerCommonOptions,
    ): Promise<void> => {
      const isMastodon =
        data.prefillPostData.profile_service === 'mastodon' ||
        data.prefillPostData.profileService === 'mastodon'

      const prefillPostData =
        isMastodon && typeof data.prefillPostData.text === 'string'
          ? {
              ...data.prefillPostData,
              text: stripMastodonMarkdownUrls(data.prefillPostData.text),
            }
          : data.prefillPostData

      const actionData: OpenComposerInput = {
        selectedProfileIds:
          data.channels === 'all' ? channels?.map((c) => c.id) : data.channels,
        cta: data.cta,
        withAIPanelOpen: options?.aiPanelOpen,
        duplicatedFrom: data?.duplicatedFrom,
        prefillPostData,
      }

      openComposer(actionData)
    },
    [channels, openComposer],
  )

  const createNewPinnedPostInComposer = useCallback(
    async (
      data: CreateNewPinnedPostInComposerData,
      options?: OpenComposerCommonOptions,
    ): Promise<void> => {
      const actionData: OpenComposerInput = {
        selectedProfileIds: data.channels,
        cta: data.cta,
        withAIPanelOpen: options?.aiPanelOpen,
        emptySlotMode: true,
        prefillPostData: {
          ...data.prefillPostData,
          due_at: data.prefillPostData.dueAt,
          scheduled_at: data.prefillPostData.dueAt,
          scheduledAt: data.prefillPostData.dueAt,
        },
      }

      openComposer(actionData)
    },
    [openComposer],
  )

  const createNewPostInComposer = useCallback(
    async (
      data: CreateNewPostInComposerData,
      options?: OpenComposerCommonOptions,
    ): Promise<void> => {
      const actionData: OpenComposerInput = {
        selectedProfileIds:
          data.channels === 'all' ? channels?.map((c) => c.id) : data.channels,
        cta: data.cta,
        returnFocusTo: data.returnFocusTo,
        withAIPanelOpen:
          options?.aiPanelOpen || options?.activeSidePanel === 'aiAssistant',
        activeSidePanel: options?.activeSidePanel,
        prefillAiPrompt: options?.prefillAiPrompt,
      }

      if (options?.prefillFromLocalStorage) {
        const draftData = getDraftDataFromLocalStorage()
        if (draftData) {
          data.prefillPostData = {
            ...data.prefillPostData,
            ...draftDataToRpcUpdate(draftData),
          }
        }
      }

      if (data?.prefillPostData) {
        actionData.prefillPostData = {
          ...data.prefillPostData,
          ...(data.prefillPostData.dueAt && {
            due_at: data.prefillPostData.dueAt,
            scheduled_at: data.prefillPostData.dueAt,
            scheduledAt: data.prefillPostData.dueAt,
          }),
        }
      }

      if (data?.templateId) {
        actionData.templateId = data.templateId
      }

      openComposer(actionData)
    },
    [channels, openComposer],
  )

  const hideComposer = useCallback((): void => {
    hideComposerAction()
  }, [hideComposerAction])

  const discardComposer = useCallback((): void => {
    hideComposerAction(true)
  }, [hideComposerAction])

  return {
    triggerAttributes,
    isOpen: open,
    editPostInComposer,
    editContentItemDraftInComposer,
    duplicatePostInComposer,
    createNewPinnedPostInComposer,
    createNewPostInComposer,
    hideComposer,
    discardComposer,
  }
}
